import { setGlobalError } from 'src/pages/auth/reducers/authSlice';
import axiosInstance, { ApiKeys, getApiUrl, getErrorMessage, logError } from 'src/utils/ApiHelper';
import {
  setHumanReminderAgentDetails,
  setHumanReminderCustomerDetails,
  setHumanReminderCustomerDetailsLoading,
  setHumanReminderCustomerImage
} from 'src/reducers/commonSlice';
import {
  setCallId,
  setDisconnectApiLoading,
  setInteractionRefId,
  setReminderRefId
} from 'src/reducers/humanReminderSlice';
import { addClickstreamEvent } from '../../../service/clickStreamEventService';
import { HRC_CLICKSTREAM, HRC_EVENTS } from '@cp/src/service/clickStream.constant';
import { ExtensionHandler } from '@cp/utils/extension.utils';
import { Dispatch } from '@reduxjs/toolkit';
import { noop } from '@utils/common';
import { AxiosError } from 'axios';
import {
  AGENT_AVAILABILITY,
  HRC_ALERTS_SLACK_CHANNEL,
  HRC_API_TIMEOUT
} from '../../DashboardV2/dc-97/dc97Constant';
import logger from '@cp/src/utils/logger';

const handleApiTimeoutAndSendEvent = (errorCode: string, ...eventAttributes: string[]) => {
  if (errorCode === AxiosError.ECONNABORTED) {
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_ACCEPT_CALL_TIMEOUT], {
      ...eventAttributes
    });
  }
};

export const setCallStatus = (
  connectedPhoneNumber: string,
  accountNumber: string,
  crtObjectId: string,
  callAcceptedTime: number,
  errorCallback: (e: any) => void,
  campaignId: string | null,
  reminderRefId: string | null,
  callId: string
) => {
  const acceptCallAudit = getApiUrl(ApiKeys.HUMAN_REMINDER_ACCEPT_V2);

  //sending audit record

  const url = getApiUrl(ApiKeys.HUMAN_REMINDER_GET_CASE_DETAILS_V2, {}, { callId: callId });

  const data = { connectedPhoneNumber, accountNumber, crtObjectId, callAcceptedTime, campaignId };
  addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_ACCEPT_CALL_MADE], {
    connectedPhoneNumber,
    reminderRefId,
    accountNumber,
    crtObjectId,
    callAcceptedTime,
    currentCallStatus: ExtensionHandler.currentCallStatus(),
    campaignId,
    ameyoStatus: ExtensionHandler.currentAmeyoStatus()
  });

  logger({
    msg: 'hrc call accepted api call',
    extras: {
      callId: callId,
      campaignId: campaignId || '',
      connectedPhoneNumber,
      reminderRefId: reminderRefId || '',
      accountNumber: accountNumber || '',
      crtObjectId: crtObjectId || ''
    }
  });

  return async function (dispatch: any) {
    await axiosInstance.post(acceptCallAudit, {
      callId: callId,
      providerCallReferenceId: crtObjectId
    });
    dispatch(setCallId(callId));
    axiosInstance
      .get(url, { headers: { donotHandleError: true }, timeout: HRC_API_TIMEOUT })
      .then(response => {
        if (response?.data?.body) {
          addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_ACCEPT_CALL_RESPONSE], {
            connectedPhoneNumber,
            accountNumber,
            crtObjectId,
            callAcceptedTime,
            response: response.data.body,
            currentCallStatus: ExtensionHandler.currentCallStatus(),
            reminderRefId,
            campaignId,
            ameyoStatus: ExtensionHandler.currentAmeyoStatus()
          });
          dispatch(
            getCustomerImage(
              response.data.body?.customerDetails?.customerReferenceId,
              response.data.body?.loanDetails?.loanAccountNumber
            )
          );
          dispatch(setHumanReminderCustomerDetails({ humanReminderResponse: response.data.body }));
          dispatch(setHumanReminderCustomerDetailsLoading(false));

          dispatch(setReminderRefId(response.data.body?.reminderRefId));
          dispatch(setInteractionRefId(response.data.body?.interactionRefId));
        }
      })
      .catch(error => {
        handleApiTimeoutAndSendEvent(
          error?.code || '',
          accountNumber,
          connectedPhoneNumber,
          crtObjectId
        );
        errorCallback(error);
        dispatch(setHumanReminderCustomerDetailsLoading(false));
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.ACCEPT_CALL_FAILED], {
          connectedPhoneNumber,
          accountNumber,
          crtObjectId,
          callAcceptedTime,
          error,
          currentCallStatus: ExtensionHandler.currentCallStatus(),
          reminderRefId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        const message = getErrorMessage(error);
        dispatch(setGlobalError({ message }));
      });
  };
};

export const setAgentAvailability = (
  agentAvailabilityStatus: AGENT_AVAILABILITY,
  callId?: string
) => {
  const url = getApiUrl(ApiKeys.HUMAN_REMINDER_AVAILABLITY_V2, {
    agentAvailabilityStatus,
    callId: callId || ''
  });
  axiosInstance.put(url, {}, { headers: { donotHandleError: true } }).catch(err => {
    logError(
      err,
      `Getting error while changing agentAvailabilityStatus to  ${agentAvailabilityStatus} for api::  ${url}`
    );
  });
};

export const setCallStatusDisconnected =
  (params: {
    humanReminderReferenceId: string;
    agentRefId: string;
    campaignId: string | null;
    cleanup: () => void;
    phoneNumber: string;
    lan: string;
    crtObjectId: string;
    callId: string;
  }) =>
  (dispatch: Dispatch) => {
    const {
      humanReminderReferenceId,
      agentRefId,
      campaignId,
      cleanup,
      lan,
      crtObjectId,
      phoneNumber,
      callId
    } = params;
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_DISCONNECT_API_CALLED], {
      humanReminderReferenceId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
    const url = getApiUrl(ApiKeys.HUMAN_REMINDER_DISCONNECT_V2);
    logger({
      msg: 'hrc call disconnected api call',
      extras: {
        callId: callId,
        campaignId: campaignId || '',
        humanReminderReferenceId: humanReminderReferenceId || '',
        agentRefId: agentRefId || '',
        phoneNumber: phoneNumber || '',
        lan: lan || '',
        crtObjectId: crtObjectId || ''
      }
    });
    if (!callId) {
      return;
    }
    dispatch(setDisconnectApiLoading(true));
    axiosInstance
      .post(
        url,
        { callId: callId },
        { headers: { donotHandleError: true }, timeout: HRC_API_TIMEOUT }
      )
      .then(() => {
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_DISCONNECT_API_SUCCESS], {
          humanReminderReferenceId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        cleanup();
        dispatch(setDisconnectApiLoading(false));
      })
      .catch(err => {
        handleApiTimeoutAndSendEvent(
          err?.code || '',
          lan,
          crtObjectId,
          phoneNumber,
          humanReminderReferenceId
        );
        addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_DISCONNECT_API_FAILURE], {
          humanReminderReferenceId,
          campaignId,
          ameyoStatus: ExtensionHandler.currentAmeyoStatus()
        });
        logError(err, 'Getting error When call is disconnected api:: ' + url);
        cleanup();
        dispatch(setDisconnectApiLoading(false));
      });
  };

export const getAgentStats = () => {
  const url = getApiUrl(ApiKeys.HUMAN_REMINDER_AGENT_STATS);
  return async function (dispatch: (arg0: { payload: any; type: string }) => void) {
    await axiosInstance
      .get(url, { headers: { donotHandleError: true } })
      .then(response => {
        if (response?.data?.body) dispatch(setHumanReminderAgentDetails(response.data.body));
      })
      .catch(err => {
        dispatch(setHumanReminderCustomerDetailsLoading(false));
        logError(err, 'Getting error from  dc-97 api:: ' + url);
      });
  };
};

export const getCustomerDetails = (callId: string, errCallback: () => void) => {
  const url = getApiUrl(ApiKeys.HUMAN_REMINDER_GET_CASE_DETAILS_V2, {}, { callId: callId });
  return async function (dispatch: any) {
    axiosInstance
      .get(url, { headers: { donotHandleError: true } })
      .then(response => {
        if (response?.data?.body) {
          dispatch(setHumanReminderCustomerDetailsLoading(false));
          dispatch(
            getCustomerImage(
              response.data.body.customerDetails.customerReferenceId,
              response.data.body.loanDetails.loanAccountNumber
            )
          );
          dispatch(setHumanReminderCustomerDetails({ humanReminderResponse: response.data.body }));
        }
      })
      .catch(err => {
        dispatch(setHumanReminderCustomerDetailsLoading(false));
        errCallback();
        logError(err, 'Getting error while fetching Customer Details :: ' + url);
      });
  };
};

export const getCustomerImage = (customerRefId: string, loanAccountNumber: string) => {
  const url = getApiUrl(ApiKeys.HRC_CUSTOMER_IMAGE);
  return async function (dispatch: any) {
    axiosInstance
      .get(url, {
        headers: {
          donotHandleError: true,
          customerReferenceId: customerRefId,
          loanAccountNumber: loanAccountNumber
        }
      })
      .then(response => {
        if (response?.data?.uri) {
          dispatch(setHumanReminderCustomerImage(response.data.uri));
        }
      })
      .catch(err => {
        dispatch(setHumanReminderCustomerDetailsLoading(false));
        logError(err, 'Getting error while fetching Customer Image :: ' + url);
      });
  };
};

export async function getCustomerDetailsRecalibrate(callId: string) {
  const url = getApiUrl(ApiKeys.HUMAN_REMINDER_GET_CASE_DETAILS_V2, {}, { callId: callId });
  return axiosInstance.get(url, { headers: { donotHandleError: true } });
}
